import { normalizePortalPath } from '../../../../utilities/normalizePortalPath';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import type { FileRouteTypes } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { Chart, PatientDoc, InstructionalVideos } from '@usacsmideo/types';
import useChart from '../../../../hooks/useChart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { Heading } from '../../../../components/Heading';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { classNames } from '../../../../style/classNames';
import { updateNavObjAndGetNextScreen } from '../../../../data/navigation/updateNavObjAndGetNextScreen';
import * as Sentry from '@sentry/browser';

export const Route = createFileRoute('/_authenticated/$patientId/statement/instructionalVideos')({
  component: InstructionalVideoPage,
});

function InstructionalVideoPage() {
  const { patientId } = Route.useParams();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;

  const { mutate: mutatePatientDoc } = useUpdateUserDoc();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentLocation = routerState.location;
  const pathname = currentLocation.pathname;

  const handleNextButtonClick = async () => {
    const res = await updateNavObjAndGetNextScreen({
      patientId,
      currentPage: pathname,
      patientInfo: patientInfo,
      chart: chart,
    });

    if ('incompleteMessage' in res) {
      // THE PATIENT HAS NOT COMPLETED THE NECESSARY SCREENS
      alert(`Incomplete Information: ${res.incompleteMessage}`);
      return;
    }
    if ('errorMessage' in res) {
      // FIRESTORE ERROR
      alert(`Error: ${res.errorMessage}`);
      return;
    }
    // NAVIGATION COMPLETE — PERSIST FINAL STATE AND NAVIGATE HOME
    if ('complete' in res) {
      mutatePatientDoc(
        { patientId: patientId, navObj: res.updatedNavObj },
        {
          onSuccess: () => {
            navigate({ to: '/' });
          },
          onError: (error: Error) => {
            Sentry.captureException(
              new Error(
                `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
              ),
            );
            console.error('Error updating patient navigation object:', error);
            alert(
              'There was an error updating your information. Please try again later or contact support if this issue persists.',
            );
          },
        },
      );
      return;
    }


    mutatePatientDoc(
      {
        patientId: patientId,
        navObj: res.updatedNavObj,
      },
      {
        onSuccess: () => {
          // NAVIGATE TO THE NEXT SCREEN
          navigate({ to: normalizePortalPath(res.nextScreen.portalPath) as FileRouteTypes['to'] });
        },
        onError: (error: Error) => {
          Sentry.captureException(
            new Error(
              `Error updating patient navigation object for patient ${patientId}: ${error.message}`,
            ),
          );
          console.error('Error updating patient navigation object:', error);
          alert(
            'There was an error updating your information. Please try again later or contact support if this issue persists.',
          );
        },
      },
    );
  };

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.PATIENT_INFO_1) {
    return (
      <>
        <div id="instructional-videos-page" className="flex w-screen flex-col items-center justify-center">
          <Heading id="instructional-videos-page-header" textContent="Instructional Videos" />
          <div id="instructional-videos-page-main" className="flex w-5/6 flex-col items-center">
            <p className="my-4 text-xl">Watch the following video for final instructions on recording your video.</p>
            {/* TODO: Add captions track and disable warning */}
            {/* eslint-disable-next-line */}
            <video id="time-to-record" className="my-4 w-1/3" controls>
              <source src={InstructionalVideos.Example.timeToRecord} type="video/mp4" />
            </video>
            <p className="my-4 text-xl">Watch the following video for an example of a MIDEO safety statement.</p>
            {/* TODO: Add captions track and disable warning */}
            {/* eslint-disable-next-line */}
            <video id="sample-mideo" className="my-4 w-1/3" controls>
              <source src={InstructionalVideos.Example.sampleMIDEO} type="video/mp4" />
            </video>
          </div>
          <div id="nav" className="flex flex-row">
            <div className="flex flex-col items-center">
              <button
                id="submit-form-info"
                title="Submit Form Info"
                onClick={handleNextButtonClick}
                disabled={false}
                className={classNames.nextButton}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
